import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, spreadOrder, getSchemes } from "../../../api/bseApi";

const FOLIO_TYPES = [
  { value: "FRESH", label: "FRESH" },
  { value: "ADDITIONAL", label: "ADDITIONAL" },
];
const DP_MODES = [
  { value: "P", label: "Physical (P)" },
  { value: "C", label: "CDSL (C)" },
  { value: "N", label: "NSDL (N)" },
];

/**
 * Get next business day (excludes Sat/Sun) in DD/MM/YYYY format
 */
function getNextBusinessDay() {
  const d = new Date();
  let day = d.getDay(); // 0=Sun, 6=Sat
  let add = 1;
  if (day === 5) add = 3; // Fri -> Mon
  if (day === 6) add = 2; // Sat -> Mon
  if (day === 0) add = 1; // Sun -> Mon
  d.setDate(d.getDate() + add);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * SpreadOrderScreen - BSE StAR MF Spread Order Entry (Overnight / Bulk Order)
 * Opens from Investments / Bulk Orders section
 * Receives: ClientCode, Scheme master list, Folio list (if any)
 */
export default function SpreadOrderScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [form, setForm] = useState({
    schemeCd: "",
    schemeCdManual: "",
    amount: "",
    folioType: "FRESH",
    folioNo: "",
    execDate: getNextBusinessDay(),
    dptxn: "P",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode || params.get("clientCode");
    if (clientCode) {
      setContext({
        clientCode,
        schemes: state?.schemes,
        folios: state?.folios || [],
      });
      setFetching(false);
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({
            clientCode: String(ucc).trim(),
            schemes: state?.schemes,
            folios: state?.folios || [],
          });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire("Error", "Missing ClientCode. Open from Investments/Bulk Orders or User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, location.search, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  useEffect(() => {
    if (context) {
      setForm((prev) => ({ ...prev, execDate: getNextBusinessDay() }));
    }
  }, [context]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
    if (!schemeCd) err.schemeCd = "Scheme selection is mandatory";

    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0) {
      err.amount = "Amount must be greater than 0";
    }

    const execDate = (form.execDate || "").trim();
    if (!execDate) {
      err.execDate = "Execution Date is mandatory";
    } else {
      const nextBiz = getNextBusinessDay();
      if (execDate !== nextBiz) {
        err.execDate = "ExecDate must be next business day only. Expected: " + nextBiz;
      }
    }

    if (form.folioType === "ADDITIONAL") {
      if (!form.folioNo?.trim()) err.folioNo = "FolioNo is mandatory when Folio Type is ADDITIONAL";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        schemeCd,
        buySellType: form.folioType,
        dptxn: form.dptxn,
        orderVal: parseFloat(form.amount),
        execDate: form.execDate.trim(),
        folioNo: form.folioType === "ADDITIONAL" ? form.folioNo.trim() : "",
        kycStatus: "Y",
        minRedeem: "N",
      };

      const res = await spreadOrder(payload);

      if (res.data?.status === 1) {
        const orderId = res.data?.data?.orderId;
        const payAmount = parseFloat(form.amount);
        Swal.fire({
          icon: "success",
          title: "Spread Order Placed",
          text: `Spread order placed successfully. Order ID: ${orderId || "N/A"}`,
        });
        navigate("/payment/mode", {
          state: { orderNo: orderId, clientCode: context.clientCode, amount: payAmount },
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Spread order failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Spread order failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;
  const nextBizDay = getNextBusinessDay();

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Spread Order</li>
              </ol>
            </nav>
            <h5>Spread Order (Overnight / Bulk Order)</h5>
            <p className="text-muted mb-0">Client Code: {context.clientCode}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Order Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Scheme (SchemeCd) <span className="text-danger">*</span></label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCdManual"
                      value={form.schemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter scheme code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCd"
                      value={form.schemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName || s.schemes || s.schemeCd} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.schemeCd && <div className="invalid-feedback">{errors.schemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Amount (OrderVal) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="e.g. 10000"
                  />
                  {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Folio Type</label>
                  <select
                    className="form-select"
                    name="folioType"
                    value={form.folioType}
                    onChange={handleChange}
                  >
                    {FOLIO_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {form.folioType === "ADDITIONAL" && (
                  <div className="col-md-6">
                    <label className="form-label">Folio No (FolioNo) <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${errors.folioNo ? "is-invalid" : ""}`}
                      name="folioNo"
                      value={form.folioNo}
                      onChange={handleChange}
                      placeholder="e.g. 12345678"
                    />
                    {context.folios?.length > 0 && (
                      <select
                        className="form-select mt-2"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) setForm((prev) => ({ ...prev, folioNo: v }));
                        }}
                      >
                        <option value="">Select from folio list</option>
                        {context.folios.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    )}
                    {errors.folioNo && <div className="invalid-feedback">{errors.folioNo}</div>}
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label">Execution Date (ExecDate) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.execDate ? "is-invalid" : ""}`}
                    name="execDate"
                    value={form.execDate}
                    onChange={handleChange}
                    placeholder="DD/MM/YYYY"
                    readOnly
                  />
                  <small className="text-muted">Next business day only: {nextBizDay}</small>
                  {errors.execDate && <div className="invalid-feedback">{errors.execDate}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">DP Transaction Mode (DPTxn)</label>
                  <select
                    className="form-select"
                    name="dptxn"
                    value={form.dptxn}
                    onChange={handleChange}
                  >
                    {DP_MODES.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Placing...
                    </>
                  ) : (
                    "Place Spread Order"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
